import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Typography, Card, CardContent, Box, Chip, Skeleton, Modal } from '@mui/material';
import { styled } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useDirectionContext } from '../contexts/DirectionContext';
import { useTranslation } from 'react-i18next';
import { colors, spacing, typography, shadows, radius, transitions } from '../theme/tokens';

const SUPERUSER_PHONE = '0548004990';

const PageContainer = styled(Box)({
  backgroundColor: colors.background.default,
  minHeight: '100vh',
  padding: `${spacing.xl} ${spacing.md}`,
});

const ContentContainer = styled('div')({
  maxWidth: '1200px',
  margin: '0 auto',
});

const PageTitle = styled(Typography)({
  fontSize: typography.fontSize['3xl'],
  fontWeight: typography.fontWeight.bold,
  color: colors.primary.main,
  marginBottom: spacing.sm,
  display: 'flex',
  alignItems: 'center',
  gap: spacing.md,
});

const PageSubtitle = styled(Typography)({
  fontSize: typography.fontSize.lg,
  color: colors.text.secondary,
  marginBottom: spacing.xl,
  fontWeight: typography.fontWeight.medium,
});

const StyledCard = styled(Card)({
  marginBottom: spacing.md,
  borderRadius: radius.lg,
  border: `2px solid ${colors.border.light}`,
  backgroundColor: colors.background.paper,
  cursor: 'pointer',
  transition: transitions.all,
  boxShadow: shadows.sm,
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: colors.secondary.main,
    // Use filter instead of boxShadow for better performance (GPU-accelerated)
    filter: 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.1)) drop-shadow(0 4px 6px rgba(0, 0, 0, 0.05))',
  },
});

const CardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.md,
  marginBottom: spacing.md,
});

const OrgIcon = styled(BusinessIcon)({
  fontSize: typography.fontSize['2xl'],
  color: colors.secondary.main,
});

const OrgName = styled(Typography)({
  fontSize: typography.fontSize.xl,
  fontWeight: typography.fontWeight.bold,
  color: colors.text.primary,
  flex: 1,
});

const MembersSection = styled(Box)({
  marginTop: spacing.md,
  paddingTop: spacing.md,
  borderTop: `1px solid ${colors.border.light}`,
});

const MembersHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  marginBottom: spacing.sm,
});

const MembersLabel = styled(Typography)({
  fontSize: typography.fontSize.base,
  fontWeight: typography.fontWeight.semibold,
  color: colors.text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
});

const MemberChip = styled(Chip)({
  margin: spacing.xs,
  backgroundColor: colors.primary.light,
  color: colors.primary.main,
  fontWeight: typography.fontWeight.semibold,
  border: `1px solid ${colors.secondary.main}`,
});

const MembersContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: spacing.sm,
  marginTop: spacing.sm,
});

const EmptyState = styled(Box)({
  textAlign: 'center',
  padding: `${spacing['2xl']} ${spacing.md}`,
  color: colors.text.secondary,
});

const ArrowIcon = styled(ArrowBackIosIcon)({
  fontSize: typography.fontSize.lg,
  color: colors.secondary.main,
});

const OrgsBoard = (props) => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [endDateModal, setEndDateModal] = useState(null); // org object or null
  const [endDateValue, setEndDateValue] = useState('');
  const [saving, setSaving] = useState(false);
  const direction = useDirectionContext();
  const { t } = useTranslation();

  const isSuperuser = props.phoneNumber === SUPERUSER_PHONE;
  const navigate = useNavigate();

  const getEndDateBadge = (endDate) => {
    if (!endDate) return { label: 'פעיל', color: '#16a34a', bg: '#dcfce7', border: '#bbf7d0' };
    const exp = new Date(endDate);
    const now = new Date();
    if (exp > now) {
      const formatted = exp.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
      return { label: `תפוגה: ${formatted}`, color: '#d97706', bg: '#fef3c7', border: '#fde68a' };
    }
    return { label: 'פג תוקף', color: '#dc2626', bg: '#fee2e2', border: '#fecaca' };
  };

  const openEndDateModal = (e, org) => {
    e.stopPropagation();
    setEndDateValue(org.endDate ? org.endDate.slice(0, 10) : '');
    setEndDateModal(org);
  };

  const handleSaveEndDate = async (endDate) => {
    if (!endDateModal) return;
    setSaving(true);
    try {
      await axios.post(`${window.AppConfig.serverDomain}/api/organzations/set-end-date/`, {
        orgID: endDateModal.orgID,
        endDate: endDate,
        phone: props.phoneNumber,
      });
      setOrgs(prev => prev.map(o =>
        o.orgID === endDateModal.orgID ? { ...o, endDate: endDate } : o
      ));
      setEndDateModal(null);
    } catch (err) {
      console.error('Error setting end date:', err);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {

    // Function to fetch data from the backend server
    const fetchData = async () => {
      try {
        setLoading(true);
        // Make the GET request using Axios
        const response = await axios.get(`${window.AppConfig.serverDomain}/api/organzations/get-orgs/${props.phoneNumber}`);
        // const response = await axios.get(`http://localhost:3001/api/organzations/get-orgs/${props.phoneNumber}`);
        console.log(response.data.result);
        setOrgs(response.data.result); // Update the state with the fetched data
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    // Call the fetchData function when the component mounts
    fetchData();

  }, []);

  return (
    <PageContainer sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>
      {/* Loading Bar */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: colors.primary.main,
          zIndex: 9999
        }} />
      )}

      <ContentContainer style={{ direction: direction }}>
        <PageTitle>
          {direction === 'ltr' ? (
            <>
              <BusinessIcon style={{ fontSize: typography.fontSize['4xl'], color: colors.primary.main, order: -1 }} />
              {t('orgsBoard.title')}
            </>
          ) : (
            <>
              <BusinessIcon style={{ fontSize: typography.fontSize['4xl'], color: colors.primary.main }} />
              {t('orgsBoard.title')}
            </>
          )}
        </PageTitle>
        <PageSubtitle sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('orgsBoard.subtitle')}</PageSubtitle>

        {loading ? (
          /* Skeleton Loading State */
          <>
            {[1, 2, 3].map((index) => (
              <StyledCard key={index} sx={{ pointerEvents: 'none' }}>
                <CardContent>
                  <CardHeader>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="60%" height={32} />
                    </Box>
                    <Skeleton variant="circular" width={24} height={24} />
                  </CardHeader>
                  <MembersSection>
                    <Skeleton variant="text" width="40%" height={24} sx={{ marginBottom: spacing.sm }} />
                    <MembersContainer>
                      <Skeleton variant="rounded" width={100} height={28} sx={{ borderRadius: radius.md }} />
                      <Skeleton variant="rounded" width={100} height={28} sx={{ borderRadius: radius.md }} />
                      <Skeleton variant="rounded" width={100} height={28} sx={{ borderRadius: radius.md }} />
                    </MembersContainer>
                  </MembersSection>
                </CardContent>
              </StyledCard>
            ))}
          </>
        ) : orgs.length === 0 ? (
          <EmptyState>
            <BusinessIcon style={{ fontSize: typography.fontSize['5xl'], color: colors.border.default, marginBottom: spacing.md }} />
            <Typography variant="h5" style={{ color: colors.text.primary, fontWeight: typography.fontWeight.bold, marginBottom: spacing.sm }}>
              {t('orgsBoard.noOrgsTitle')}
            </Typography>
            <Typography variant="body1" style={{ color: colors.text.secondary, marginBottom: spacing.xl, maxWidth: '500px', margin: `0 auto ${spacing.xl}` }}>
              {t('orgsBoard.noOrgsMessage')}
            </Typography>
            <Typography variant="body2" style={{
              color: colors.text.muted,
              fontSize: typography.fontSize.sm,
              padding: `${spacing.md} ${spacing.lg}`,
              backgroundColor: colors.background.default,
              borderRadius: radius.md,
              border: `1px solid ${colors.border.light}`,
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              💡 <strong>{direction === 'rtl' ? 'טיפ:' : 'Tip:'}</strong> {t('orgsBoard.noOrgsTip')}
            </Typography>
          </EmptyState>
        ) : (
          orgs.map((org, index) => (
            <StyledCard key={index} onClick={() => navigate(`/org/${org.orgID}`)}>
              <CardContent>
                <CardHeader>
                  {direction === 'ltr' ? (
                    <>
                      <OrgIcon style={{ order: -2 }} />
                      <OrgName sx={{ textAlign: 'left', order: -1 }}>{org.orgName}</OrgName>
                      {isSuperuser && (() => { const badge = getEndDateBadge(org.endDate); return (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px', background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`, whiteSpace: 'nowrap' }}>{badge.label}</span>
                          <button onClick={(e) => openEndDateModal(e, org)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px', color: colors.text.secondary, display: 'flex' }} title="Set end date"><CalendarTodayIcon style={{ fontSize: '18px' }} /></button>
                        </span>
                      ); })()}
                      <ArrowIcon sx={{ transform: 'rotate(180deg)', order: 0 }} />
                    </>
                  ) : (
                    <>
                      <OrgIcon />
                      <OrgName sx={{ textAlign: 'right' }}>{org.orgName}</OrgName>
                      {isSuperuser && (() => { const badge = getEndDateBadge(org.endDate); return (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button onClick={(e) => openEndDateModal(e, org)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px', color: colors.text.secondary, display: 'flex' }} title="Set end date"><CalendarTodayIcon style={{ fontSize: '18px' }} /></button>
                          <span style={{ fontSize: '12px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px', background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`, whiteSpace: 'nowrap' }}>{badge.label}</span>
                        </span>
                      ); })()}
                      <ArrowIcon />
                    </>
                  )}
                </CardHeader>

                {org.members && org.members.length > 0 && (
                  <MembersSection>
                    <MembersLabel>
                      <PeopleIcon style={{ fontSize: typography.fontSize.lg, color: colors.secondary.main }} />
                      {t('orgsBoard.members')} ({org.members.length})
                    </MembersLabel>
                    <MembersContainer>
                      {org.members.map((member, idx) => (
                        <MemberChip
                          key={idx}
                          label={`0${member.slice(4)}`}
                          size="small"
                          sx={{ direction: 'ltr' }} // Phone numbers are always LTR
                        />
                      ))}
                    </MembersContainer>
                  </MembersSection>
                )}
              </CardContent>
            </StyledCard>
          ))
        )}
      </ContentContainer>

      {/* End Date Modal — superuser only */}
      <Modal open={!!endDateModal} onClose={() => setEndDateModal(null)}>
        <Box onClick={e => e.stopPropagation()} sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#fff', borderRadius: '12px',
          padding: '28px', width: '320px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          outline: 'none'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: colors.text.primary }}>
            תאריך תפוגה
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 2 }}>
            {endDateModal?.orgName}
          </Typography>
          <input
            type="date"
            value={endDateValue}
            onChange={e => setEndDateValue(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px', fontSize: '15px',
              border: `1px solid ${colors.border.default}`, borderRadius: '8px',
              marginBottom: '16px', boxSizing: 'border-box', outline: 'none'
            }}
          />
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => handleSaveEndDate(null)}
              disabled={saving}
              style={{
                flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${colors.border.default}`,
                background: '#f8fafc', color: colors.text.secondary, cursor: 'pointer', fontWeight: 600, fontSize: '14px'
              }}
            >
              נקה
            </button>
            <button
              onClick={() => handleSaveEndDate(endDateValue || null)}
              disabled={saving || !endDateValue}
              style={{
                flex: 2, padding: '10px', borderRadius: '8px', border: 'none',
                background: endDateValue && !saving ? colors.primary.main : '#cbd5e1',
                color: '#fff', cursor: endDateValue && !saving ? 'pointer' : 'not-allowed',
                fontWeight: 600, fontSize: '14px'
              }}
            >
              {saving ? 'שומר...' : 'שמור'}
            </button>
          </Box>
        </Box>
      </Modal>
    </PageContainer>
  );
};

export default OrgsBoard;
